import { computed, onActivated, onDeactivated, onScopeDispose, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { fetchCoinGiveStatus } from '@/api/balance';
import { useBalanceStore, useUserStore } from '@/store';
import {
  markDashboardDialogsReady,
  setCoinGiftDialogOpen,
  useDashboardDialogCoordinator
} from '@/hooks/useDashboardDialogCoordinator';

const COIN_GIFT_AUTO_SHOWN_KEY_PREFIX = 'aipicturebook:coin-gift:auto-shown:v1';
const canReceiveCoinGift = ref(false);
const coinGiftStatusUserId = ref(0);
const autoShownUserIds = new Set<number>();
const statusRequests = new Map<number, Promise<boolean>>();
const latestStatusRequestIds = new Map<number, number>();
let nextStatusRequestId = 0;
let stopWatchingCoinGiftDialog: (() => void) | undefined;

export function useCoinGift() {
  const balanceStore = useBalanceStore();
  const userStore = useUserStore();
  const { coinGiftDialogOpen, onboardingDialogOpen } = useDashboardDialogCoordinator();
  let isActive = true;

  function getCurrentUserId() {
    const userId = Number(userStore.userData.userId);
    return userStore.isLogin && userId > 0 ? userId : 0;
  }

  function getAutoShownKey(userId: number) {
    return `${COIN_GIFT_AUTO_SHOWN_KEY_PREFIX}:${userId}`;
  }

  function hasAutoShown(userId: number) {
    if (autoShownUserIds.has(userId)) return true;

    try {
      return window.localStorage.getItem(getAutoShownKey(userId)) === '1';
    } catch (error) {
      console.warn(error, 'read coin gift auto shown status error');
      return false;
    }
  }

  function markAutoShown(userId: number) {
    autoShownUserIds.add(userId);

    try {
      window.localStorage.setItem(getAutoShownKey(userId), '1');
    } catch (error) {
      console.warn(error, 'save coin gift auto shown status error');
    }
  }

  function setCoinGiftStatus(userId: number, eligible: boolean) {
    coinGiftStatusUserId.value = userId;
    canReceiveCoinGift.value = eligible;
  }

  async function refreshCoinGiftStatus(force = false) {
    const userId = getCurrentUserId();
    if (!userId) {
      setCoinGiftStatus(0, false);
      return false;
    }

    if (coinGiftStatusUserId.value !== userId) {
      setCoinGiftStatus(userId, false);
    }

    const pendingRequest = statusRequests.get(userId);
    if (pendingRequest && !force) return pendingRequest;

    setCoinGiftStatus(userId, false);
    const requestId = nextStatusRequestId + 1;
    nextStatusRequestId = requestId;
    latestStatusRequestIds.set(userId, requestId);

    const request = (async () => {
      try {
        const data = await fetchCoinGiveStatus();
        if (latestStatusRequestIds.get(userId) !== requestId) return false;

        const eligible = data.status === 0;
        if (getCurrentUserId() === userId) {
          setCoinGiftStatus(userId, eligible);
        }
        return eligible;
      } catch (error) {
        console.error(error, 'refreshCoinGiftStatus error');
        if (latestStatusRequestIds.get(userId) === requestId && getCurrentUserId() === userId) {
          setCoinGiftStatus(userId, false);
        }
        return false;
      }
    })();
    statusRequests.set(userId, request);
    const clearRequest = () => {
      if (statusRequests.get(userId) === request) {
        statusRequests.delete(userId);
      }
    };
    void request.then(clearRequest, clearRequest);

    return request;
  }

  function openCoinGiftDialog(notifyOnUnavailable = false) {
    const userId = getCurrentUserId();
    if (!userId) return false;
    if (onboardingDialogOpen.value) return false;
    if (coinGiftDialogOpen.value) return true;

    if (!window.GetCoinDialog) {
      if (notifyOnUnavailable) {
        ElMessage.warning('领取弹窗暂未加载，请刷新页面后重试');
      }
      return false;
    }

    try {
      const refreshAfterClaim = () => {
        if (getCurrentUserId() !== userId) return;
        console.log('refreshAfterClaim');
        void balanceStore.refreshBalance();
        void refreshCoinGiftStatus(true);
      };

      const refreshAfterCreate = () => {
        if (getCurrentUserId() !== userId) return;
        console.log('refreshAfterCreate');
        void balanceStore.refreshBalance();
        void refreshCoinGiftStatus(true);
      };
      const handleClose = () => {
        stopWatchingCoinGiftDialog?.();
        stopWatchingCoinGiftDialog = undefined;
        setCoinGiftDialogOpen(false);
        markDashboardDialogsReady();
      };
      stopWatchingCoinGiftDialog = watchCoinGiftDialogClose(handleClose);
      setCoinGiftDialogOpen(true);
      new window.GetCoinDialog().show({
        onClaim: refreshAfterClaim,
        // 覆盖外部组件的默认页面刷新，领取后仅同步当前页面的余额和领取状态。
        onCreate: refreshAfterCreate,
        onClose: handleClose,
        closeCallback: handleClose
      });
      markAutoShown(userId);
      return true;
    } catch (error) {
      stopWatchingCoinGiftDialog?.();
      stopWatchingCoinGiftDialog = undefined;
      setCoinGiftDialogOpen(false);
      console.error(error, 'openCoinGiftDialog error');
      if (notifyOnUnavailable) {
        ElMessage.warning('领取弹窗暂未加载，请刷新页面后重试');
      }
      return false;
    }
  }

  async function checkAndOpenCoinGift() {
    const userId = getCurrentUserId();
    if (!userId) return false;
    if (coinGiftDialogOpen.value) return true;

    const eligible = await refreshCoinGiftStatus();
    if (!isActive || !eligible || getCurrentUserId() !== userId || hasAutoShown(userId)) {
      return false;
    }

    const opened = openCoinGiftDialog();
    return opened;
  }

  const canCurrentUserReceiveCoinGift = computed(() => {
    const userId = getCurrentUserId();
    return userId > 0 && coinGiftStatusUserId.value === userId && canReceiveCoinGift.value;
  });

  watch(getCurrentUserId, (userId) => {
    const userChanged = coinGiftStatusUserId.value !== userId;
    setCoinGiftStatus(userId, false);
    if (isActive && userId) void refreshCoinGiftStatus(userChanged);
  });

  onActivated(() => {
    isActive = true;
  });

  onDeactivated(() => {
    isActive = false;
  });

  onScopeDispose(() => {
    isActive = false;
  });

  return {
    canReceiveCoinGift: canCurrentUserReceiveCoinGift,
    refreshCoinGiftStatus,
    openCoinGiftDialog,
    checkAndOpenCoinGift
  };
}

function watchCoinGiftDialogClose(onClose: () => void) {
  const existingElements = new Set(document.body.querySelectorAll('*'));
  let dialogElements: HTMLElement[] = [];
  let hasBeenVisible = false;
  let stopped = false;

  const isVisible = (element: HTMLElement) => {
    const style = window.getComputedStyle(element);
    return (
      element.isConnected &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      Number(style.opacity) !== 0 &&
      element.getClientRects().length > 0
    );
  };

  const checkClosed = () => {
    if (stopped) return;
    dialogElements = Array.from(document.body.querySelectorAll('*')).filter(
      (element): element is HTMLElement =>
        element instanceof HTMLElement && !existingElements.has(element)
    );
    hasBeenVisible ||= dialogElements.some(isVisible);
    if (hasBeenVisible && dialogElements.every((element) => !isVisible(element))) {
      stop();
      onClose();
    }
  };

  const observer = new MutationObserver(checkClosed);
  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden'],
    childList: true,
    subtree: true
  });
  const interval = window.setInterval(checkClosed, 200);

  function stop() {
    if (stopped) return;
    stopped = true;
    window.clearInterval(interval);
    observer.disconnect();
  }

  return stop;
}
